import { Card, CardContent, CardHeader, CardTitle } from '@agentselly/ui';
import { EmptyState } from '~/components/empty/EmptyState';
import {
  cockpitActivityDesignDataConfig,
  getCockpitActivityTitle,
  getCockpitActivityActor,
} from '~/components/widget/cockpit/cockpitActivityDesignDataConfig';
import { useTranslate } from '@tolgee/react';
import { formatDate } from '~/helper/formatDate';
import { clsx } from 'clsx';
import { formatWeekday } from '~/helper/formatWeedDate';
import { useAuthz } from '~/context/AuthzContext';
import { formatTime } from '~/helper/formatTime';
import { User2, Mail, Phone, Activity } from 'lucide-react';

interface Activity {
  readonly date: number;
  readonly type: string;
  readonly count?: number;
  readonly person?: string;
  readonly email?: string;
  readonly phone?: string;
  readonly data?: Record<string, unknown>;
  readonly actorDetails?: {
    firstname?: string;
    lastname?: string;
    photoUrl?: string;
    email?: string;
    mobile?: string;
    phone?: string;
  };
  readonly by?: string;
  readonly bookedBy?: string;
  readonly hostedBy?: string;
}

interface Props {
  readonly activities: Record<string, Activity[]> | Activity[];
  readonly title?: string;
  readonly infoText?: string;
}

export function CockpitActivitiesDetailWidget(props: Props) {
  const { t } = useTranslate();
  const { atLeast } = useAuthz();
  const showDetails = atLeast('partner');

  // Handle both array and object formats
  const activityEntries = Array.isArray(props.activities)
    ? props.activities.map((activity) => [
        String(activity.date),
        [activity],
      ])
    : Object.entries(props.activities || {});

  if (!activityEntries.length) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">
            {t(props.title || 'cockpit_activities_empty_title', 'Aktivitäten')}
          </CardTitle>
          {props.infoText && (
            <p className="text-sm text-muted-foreground">
              {t(
                props.infoText,
                'Hier sehen Sie alle Aktivitäten zu Ihrer Immobilie im Detail.',
              )}
            </p>
          )}
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <EmptyState
            icon={
              <Activity className="w-8 h-8 text-gray-400 dark:text-gray-500 dark:text-gray-400" />
            }
            description={t(
              'cockpit_activities_empty_message',
              'Noch keine Aktivitäten vorhanden',
            )}
            compact
          />
        </CardContent>
      </Card>
    );
  }

  const totalCount = Array.isArray(props.activities)
    ? props.activities.length
    : Object.values(props.activities || {}).flat().length;

  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-semibold">
            {t(
              props.title || 'cockpit_activities_detail_widget_title',
              'Aktivitäten',
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">
              {totalCount}{' '}
              {totalCount === 1
                ? t('activities_count_singular', 'Aktivität')
                : t('activities_count_text', 'Aktivitäten')}
            </span>
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Activity className="h-4 w-4 text-primary" />
            </div>
          </div>
        </div>
        {props.infoText && (
          <p className="text-sm text-muted-foreground">
            {t(
              props.infoText,
              'Hier sehen Sie alle Aktivitäten zu Ihrer Immobilie im Detail.',
            )}
          </p>
        )}
      </CardHeader>
      <CardContent className="flex-1 pt-0">
        <div className="space-y-6">
          {activityEntries.map(
            (
              [
                timestamp,
                activities,
              ],
              idx,
            ) => {
              const date = new Date(parseInt(String(timestamp), 10));
              const list = Array.isArray(activities) ? activities : [];

              return (
                <div
                  key={idx}
                  className="border-l-2 border-gray-200 dark:border-gray-700 pl-4 relative"
                >
                  <div className="absolute -left-2 top-1 h-4 w-4 rounded-full bg-white dark:bg-gray-800 dark:bg-gray-900 border-2 border-primary-400" />
                  <div className="mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {formatWeekday(date)}, {formatDate(date)}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {list.length}{' '}
                      {list.length === 1
                        ? t('activities_count_singular', 'Aktivität')
                        : t('activities_count_text', 'Aktivitäten')}
                    </p>
                  </div>
                  <div className="space-y-3">
                    {list
                      .slice()
                      .sort((a, b) => {
                        const ta = (a?.date ??
                          parseInt(timestamp as string)) as number;
                        const tb = (b?.date ??
                          parseInt(timestamp as string)) as number;
                        return ta - tb;
                      })
                      .map((event, i) => {
                        const timeStr = formatTime(
                          event?.date ?? parseInt(timestamp as string),
                        );
                        const activityTitle = getCockpitActivityTitle(
                          t,
                          event?.type,
                        );

                        const rawActor =
                          event?.actorDetails?.firstname &&
                          event?.actorDetails?.lastname
                            ? `${event.actorDetails.firstname} ${event.actorDetails.lastname}`.trim()
                            : event?.by || event?.person || event?.bookedBy;

                        const actorName = getCockpitActivityActor(
                          t,
                          event?.type,
                          rawActor,
                        );

                        return (
                          <div
                            key={i}
                            className="group hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800 dark:bg-gray-800/50 dark:hover:bg-gray-800/50 p-3 rounded-lg transition-colors duration-200 -mx-3"
                          >
                            <div className="flex items-start gap-3">
                              <div
                                className={clsx(
                                  'h-10 w-10 rounded-lg flex items-center justify-center',
                                  event?.hostedBy === 'internal'
                                    ? 'bg-primary-100 text-primary-600'
                                    : 'bg-amber-100 text-amber-600',
                                )}
                              >
                                {(() => {
                                  const dd =
                                    cockpitActivityDesignDataConfig[
                                      event?.type
                                    ] ||
                                    cockpitActivityDesignDataConfig.default;
                                  const Icon = dd.icon;
                                  return <Icon className="h-4 w-4" />;
                                })()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                      {activityTitle}
                                    </span>
                                    <span className="text-xs text-muted-foreground tabular-nums">
                                      {timeStr}
                                    </span>
                                  </div>
                                </div>

                                {/* Actor/Person Information */}
                                <div className="flex items-center gap-2 mt-1">
                                  {event?.actorDetails?.photoUrl ? (
                                    <img
                                      src={event.actorDetails.photoUrl}
                                      alt="Actor"
                                      className="h-6 w-6 rounded-full object-cover"
                                    />
                                  ) : (
                                    <div className="h-6 w-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                                      <User2 className="h-3 w-3 text-gray-500 dark:text-gray-400" />
                                    </div>
                                  )}
                                  <span className="text-sm text-gray-700 dark:text-gray-200 dark:text-gray-300 font-medium">
                                    {actorName}
                                  </span>
                                </div>

                                {/* Contact Details - Only for Partners+ */}
                                {showDetails &&
                                  (event?.actorDetails ||
                                    event?.email ||
                                    event?.phone) && (
                                    <div className="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-400">
                                      {(event?.actorDetails?.email ||
                                        event?.email) && (
                                        <div className="flex items-center gap-1">
                                          <Mail className="h-3 w-3" />
                                          <a
                                            href={`mailto:${
                                              event.actorDetails?.email ||
                                              event.email
                                            }`}
                                            className="hover:text-primary-600"
                                          >
                                            {event.actorDetails?.email ||
                                              event.email}
                                          </a>
                                        </div>
                                      )}
                                      {(event?.actorDetails?.mobile ||
                                        event?.actorDetails?.phone ||
                                        event?.phone) && (
                                        <div className="flex items-center gap-1">
                                          <Phone className="h-3 w-3" />
                                          <a
                                            href={`tel:${
                                              event.actorDetails?.mobile ||
                                              event.actorDetails?.phone ||
                                              event.phone
                                            }`}
                                            className="hover:text-primary-600"
                                          >
                                            {event.actorDetails?.mobile ||
                                              event.actorDetails?.phone ||
                                              event.phone}
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                  )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              );
            },
          )}
        </div>
      </CardContent>
    </Card>
  );
}
